import { effect, inject, Injectable, NgZone } from "@angular/core";
import posthog from "posthog-js";
import { APP_CONFIG } from "../constants";
import { AuthStore } from "../+state/auth";

const UNKNOWN_COMPANY = "Unknown Company";

/** Person properties attached to the PostHog profile on identify. */
export interface PosthogUserProperties {
  email?: string;
  name?: string;
  tenant?: string | null;
  [key: string]: unknown;
}

/**
 * Owns the PostHog session and keeps its identity in step with {@link AuthStore}.
 *
 * Identity is driven by signals rather than by the auth flow calling in: the
 * person is identified whenever the Auth0 user is set, and the `company` group
 * follows the primary organization, so switching organization re-groups the
 * session without the store or the app initializer knowing about analytics.
 * All three Agridence frontends share one PostHog project per environment, so
 * the property names (`email`, `company`, `orgId`) match the Supplier Portal
 * and PMD to keep cross-app filters working.
 *
 * Refetching the member profile hands back an equal-but-new organization
 * object, and `posthog.group` bills a `$groupidentify` event on every call, so
 * what was last sent is tracked and only a real change is forwarded.
 */
@Injectable({
  providedIn: "root",
})
export class PosthogService {
  readonly #ngZone = inject(NgZone);
  readonly #appConfig = inject(APP_CONFIG);
  readonly #authStore = inject(AuthStore);

  #lastPerson: string | null = null;
  #lastCompany: string | null = null;

  constructor() {
    this.initPostHog();
    effect(() => this.syncPerson());
    effect(() => this.syncCompany());
  }

  private get enabled(): boolean {
    return !!this.#appConfig.postHog;
  }

  private initPostHog() {
    if (!this.enabled) {
      return;
    }
    this.#ngZone.runOutsideAngular(() => {
      posthog.init(this.#appConfig.postHog!.key, {
        api_host: this.#appConfig.postHog!.host,
        debug: false,
        cross_subdomain_cookie: false,
        capture_pageview: "history_change",
      });
    });
  }

  private syncPerson() {
    const user = this.#authStore.user();
    if (!user?.sub) {
      return;
    }
    const tenant = this.#authStore.tenant();
    const key = JSON.stringify([user.sub, user.email, user.name, tenant]);
    if (key === this.#lastPerson) {
      return;
    }
    this.#lastPerson = key;
    this.identifyUser(user.sub, { email: user.email, name: user.name, tenant });
  }

  private syncCompany() {
    const organization = this.#authStore.primaryOrganization();
    if (!organization?.orgId) {
      return;
    }
    const tenant = this.#authStore.tenant();
    const key = JSON.stringify([organization.orgId, organization.companyName, tenant]);
    if (key === this.#lastCompany) {
      return;
    }
    this.#lastCompany = key;
    this.setCompanyGroup(organization.orgId, organization.companyName, tenant);
  }

  /**
   * Associates the session with a known person. `email` and `tenant` are also
   * registered as super properties so every event carries them.
   */
  identifyUser(userId?: string | number | null, properties?: PosthogUserProperties) {
    if (!this.enabled || userId == null || userId === "") {
      return;
    }
    this.#ngZone.runOutsideAngular(() => {
      try {
        posthog.identify(String(userId), properties);
        const superProperties = pickDefined({ email: properties?.email, tenant: properties?.tenant });
        if (Object.keys(superProperties).length > 0) {
          posthog.register(superProperties);
        }
      } catch (error) {
        console.error("Failed to identify user in PostHog:", error);
      }
    });
  }

  /**
   * Associates the session with a `company` group keyed by the Forms
   * organization id, and registers `orgId`/`company` as super properties.
   *
   * Super properties persist until overwritten, so `company` is always written
   * — omitting it for a nameless organization would leave the previous
   * organization's name on every subsequent event.
   */
  setCompanyGroup(orgId?: string | null, companyName?: string | null, tenant?: string | null) {
    if (!this.enabled || !orgId) {
      return;
    }
    const company = companyName ?? UNKNOWN_COMPANY;
    this.#ngZone.runOutsideAngular(() => {
      try {
        posthog.group("company", orgId, pickDefined({ name: company, id: orgId, tenant }));
        posthog.register({ orgId, company });
      } catch (error) {
        console.error("Failed to set PostHog company group:", error);
      }
    });
  }

  /** Drops the identified person and super properties so the next login on this browser starts anonymous. */
  reset() {
    this.#lastPerson = null;
    this.#lastCompany = null;
    if (!this.enabled) {
      return;
    }
    this.#ngZone.runOutsideAngular(() => {
      try {
        posthog.reset();
      } catch (error) {
        console.error("Failed to reset PostHog session:", error);
      }
    });
  }
}

function pickDefined(properties: Record<string, unknown>): Record<string, unknown> {
  return Object.fromEntries(Object.entries(properties).filter(([, value]) => value != null));
}
